import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaBars, FaTimes, FaImage, FaSpinner } from 'react-icons/fa';
import './Gallery.css';

// Import Hedera utilities
import { fetchHederaCollection, getArtistIdFromSerialNumber } from './utils/hederaUtils';



const Gallery = ({ selectedArtist }) => {
    // State for Hedera NFTs
    const [hederaNfts, setHederaNfts] = useState([]);
    const [isLoadingHedera, setIsLoadingHedera] = useState(false);
    const [hederaError, setHederaError] = useState(null);
    const HEDERA_COLLECTION_ID = '0.0.6299009'; // The specific collection ID
    // eslint-disable-next-line no-unused-vars
    const [artists, setArtists] = useState([
        { artist_id: 1, artist_name: 'Varda Veil', folder: 'varda-veil/', bio: 'Varda Veil creates ethereal digital landscapes that blend natural elements with abstract forms.' },
        { artist_id: 2, artist_name: 'Soluna', folder: 'soluna', bio: 'Soluna explores the intersection of celestial imagery and digital art, creating cosmic-inspired pieces.' },
        { artist_id: 3, artist_name: 'Watusi', folder: 'watusi', bio: 'Watusi specializes in dynamic digital animations that capture movement and emotion in unexpected ways.' },
        { artist_id: 4, artist_name: 'litterthanlit', folder: 'litterthanlit', bio: 'litterthanlit creates minimalist digital art that explores the boundaries between simplicity and complexity.' }
    ]);
    const [selectedArtistId, setSelectedArtistId] = useState(selectedArtist);
    // eslint-disable-next-line no-unused-vars
    const [artworks, setArtworks] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [featuredArtwork, setFeaturedArtwork] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Add a timeout to hide loading screen even if images don't load
    useEffect(() => {
        if (loading) {
            const timer = setTimeout(() => {
                setLoading(false);
            }, 3000); // 3 seconds timeout
            return () => clearTimeout(timer);
        }
    }, [loading]);
    
    // Scroll to top when gallery loads
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    // Get all artworks for the hero section
    const [allArtworks, setAllArtworks] = useState([]);

    const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Initialize all artworks for the hero section and artist galleries
    useEffect(() => {
        // Create a collection of all artworks from all artists
        const allArtworkCollection = [];
        
        artists.forEach(artist => {
            let artworkFiles = [];
            if (artist.artist_name === 'Varda Veil') {
                artworkFiles = Array.from({ length: 16 }, (_, i) => ({
                    file: `${i + 1}.jpg`,
                    price: (i === 2 || i === 5 || i === 15) ? 'SOLD' : 'Not Listed',
                    buttonText: (i === 5) ? 'Buy on Sentx' : null,
                    buttonUrl: (i === 5) ? 'https://sentx.io/nft-marketplace/immuta-collection-vol-1' : null,
                    soldStatus: (i === 2 || i === 15) ? 'SOLD' : null
                }));
            } else if (artist.artist_name === 'Soluna') {
                artworkFiles = Array.from({ length: 2 }, (_, i) => ({
                    file: `${i + 1}.png`,
                    price: '$100',
                    buttonText: 'Buy on Sentx',
                    buttonUrl: 'https://sentx.io/nft-marketplace/immuta-collection-vol-1'
                }));
            } else if (artist.artist_name === 'Watusi') {
                artworkFiles = [{
                    file: '1.mp4',
                    price: 'Not Listed'
                }];
            } else if (artist.artist_name === 'litterthanlit') {
                artworkFiles = Array.from({ length: 2 }, (_, i) => ({
                    file: `${i + 1}.png`,
                    price: 'Not Listed',
                    buttonText: 'Buy on Sentx',
                    buttonUrl: 'https://sentx.io/nft-marketplace/immuta-collection-vol-1'
                }));
            }
            
            // Only add image files (not videos) to the collection for the hero
            const artistArtworks = artworkFiles
                .filter(artwork => !artwork.file.endsWith('.mp4'))
                .map((artwork, index) => ({
                    artwork_id: `${artist.artist_id}-${index + 1}`,
                    artist_id: artist.artist_id,
                    artist_name: artist.artist_name,
                    image_file: `/artworks/${artist.folder}/${artwork.file}`,
                    price: artwork.price,
                    buttonText: artwork.buttonText,
                    buttonUrl: artwork.buttonUrl,
                    soldStatus: artwork.soldStatus
                }));
                
            allArtworkCollection.push(...artistArtworks);
        });
        
        setAllArtworks(allArtworkCollection);
        
        // Select a random artwork for the hero section
        if (allArtworkCollection.length > 0) {
            const randomIndex = Math.floor(Math.random() * allArtworkCollection.length);
            setFeaturedArtwork(allArtworkCollection[randomIndex]);
        }
    }, [artists]);
    
    // Set artworks for the selected artist
    useEffect(() => {
        if (selectedArtistId) {
            setLoading(true); // Reset loading state when changing artist
            const selectedArtist = artists.find(artist => artist.artist_id === selectedArtistId);
            
            if (selectedArtist) {
                // Filter the all artworks collection to get only the selected artist's works
                const artistArtworks = allArtworks.filter(artwork => artwork.artist_id === selectedArtistId);
                setArtworks(artistArtworks);
            }
        }
    }, [selectedArtistId, artists, allArtworks]);

    const handleImageLoad = () => {
        setLoading(false); // Using the correct state setter
    };

    const handleImageError = () => {
        // Just set loading to false since we don't have a loadError state at this level
        setLoading(false); // Using the correct state setter
    };

    const toggleDropdown = () => {
        setDropdownVisible(!dropdownVisible);
    };
    
    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    // Shuffle all artworks for random display
    // eslint-disable-next-line no-unused-vars
    const shuffledArtworks = useMemo(() => {
        if (allArtworks.length === 0) return [];
        const shuffled = [...allArtworks];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }, [allArtworks]);

    // State for featured NFT
    const [featuredNft, setFeaturedNft] = useState(null);
    
    // Fetch Hedera NFTs on component mount
    useEffect(() => {
        const loadHederaNfts = async () => {
            setIsLoadingHedera(true);
            setHederaError(null);
            
            try {
                const result = await fetchHederaCollection(HEDERA_COLLECTION_ID);
                setHederaNfts(result.nfts);
                
                // Select a random NFT to feature
                if (result.nfts.length > 0) {
                    const randomIndex = Math.floor(Math.random() * result.nfts.length);
                    setFeaturedNft(result.nfts[randomIndex]);
                }
            } catch (error) {
                console.error('Failed to load Hedera NFTs:', error);
                setHederaError('Failed to load NFT collection. Please try again later.');
            } finally {
                setIsLoadingHedera(false);
            }
        };
        
        loadHederaNfts();
    }, []);
    
    // NFT Item Component to handle loading states properly
const HederaNftItem = ({ nft, index }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    
    const renderMedia = () => {
        if (nft.type === 'video') {
            return (
                <div className="media-container">
                    {isLoading && !loadError && (
                        <div className="image-loading-indicator">
                            <FaSpinner className="spinner-icon" />
                        </div>
                    )}
                    {loadError ? (
                        <div className="nft-placeholder-image">
                            <span>Video Error</span>
                        </div>
                    ) : (
                        <video 
                            src={nft.imageUrl}
                            controls
                            loop
                            muted
                            playsInline
                            className="nft-media"
                            style={{ opacity: isLoading ? 0 : 1 }}
                            onLoadedData={() => setIsLoading(false)}
                            onError={(e) => {
                                console.error('Video load error:', e);
                                setIsLoading(false);
                                setLoadError(true);
                            }}
                        />
                    )}
                </div>
            );
        } else if (nft.type === 'svg') {
            // For SVGs, we need to handle loading differently
            return (
                <div className="media-container">
                    {isLoading && !loadError && (
                        <div className="image-loading-indicator">
                            <FaSpinner className="spinner-icon" />
                        </div>
                    )}
                    {loadError ? (
                        <div className="nft-placeholder-image">
                            <span>SVG Error</span>
                        </div>
                    ) : (
                        <img 
                            src={nft.imageUrl} 
                            alt={nft.name}
                            className="nft-media svg-image"
                            style={{ opacity: isLoading ? 0 : 1 }}
                            onLoad={() => setIsLoading(false)}
                            onError={(e) => {
                                console.error('SVG load error:', e);
                                setIsLoading(false);
                                setLoadError(true);
                            }}
                        />
                    )}
                </div>
            );
        } else if (nft.imageUrl) {
            return (
                <div className="media-container">
                    {isLoading && !loadError && (
                        <div className="image-loading-indicator">
                            <FaSpinner className="spinner-icon" />
                        </div>
                    )}
                    <img 
                        src={nft.imageUrl} 
                        alt={nft.name}
                        className="nft-media"
                        style={{ opacity: isLoading ? 0 : 1 }}
                        onLoad={() => setIsLoading(false)}
                        onError={(e) => {
                            console.error('Image load error:', e);
                            setIsLoading(false);
                            e.target.onerror = null;
                            e.target.src = `https://placehold.co/400x400/eee/999?text=NFT+${nft.serialNumber}`;
                        }}
                    />
                </div>
            );
        } else {
            return (
                <div className="nft-placeholder-image">
                    <FaImage className="placeholder-icon" />
                    <p>No image available</p>
                </div>
            );
        }
    };
    
    return (
        <motion.div 
            key={`${nft.tokenId}-${nft.serialNumber}`}
            className="hedera-nft-item"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
        >
            <div className="hedera-nft-image">
                {renderMedia()}
            </div>
            <div className="hedera-nft-info">
                <h3 className="hedera-nft-title">{nft.name || `NFT #${nft.serialNumber}`}</h3>
                <p className="hedera-nft-serial">#{nft.serialNumber}</p>
                {nft.description && (
                    <p className="hedera-nft-description">{nft.description.substring(0, 50)}{nft.description.length > 50 ? '...' : ''}</p>
                )}
            </div>
        </motion.div>
    );
};

// Render Hedera NFTs
const renderHederaNfts = () => {
    if (isLoadingHedera) {
        return Array.from({ length: 8 }, (_, index) => (
            <motion.div 
                key={`placeholder-${index}`}
                className="nft-placeholder"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
            >
                <div className="nft-placeholder-image">
                    <FaSpinner className="spinner-icon" />
                </div>
                <div className="nft-placeholder-info">
                    <div className="placeholder-title"></div>
                    <div className="placeholder-artist"></div>
                </div>
            </motion.div>
        ));
    }
    
    if (hederaError) {
        return (
            <div className="hedera-error-message">
                {hederaError}
            </div>
        );
    }
    
    if (hederaNfts.length === 0) {
        return (
            <div className="hedera-empty-message">
                No NFTs found in this collection.
            </div>
        );
    }
    
    // Filter NFTs by artist if an artist is selected
    const filteredNfts = selectedArtistId 
        ? hederaNfts.filter(nft => {
            const artistId = getArtistIdFromSerialNumber(nft.serialNumber);
            
            // Special case for Soluna (artist_id: 2) - show the same NFTs as Watusi (artist_id: 3)
            if (selectedArtistId === 2 && artistId === 3) {
                return true;
            }
            
            return artistId === selectedArtistId;
        })
        : hederaNfts;
    
    console.log('Rendering NFTs:', filteredNfts);
    
    if (filteredNfts.length === 0) {
        return (
            <div className="hedera-empty-message">
                No NFTs found for this artist.
            </div>
        );
    }
    
    return filteredNfts.map((nft, index) => (
        <HederaNftItem key={`${nft.tokenId}-${nft.serialNumber}`} nft={nft} index={index} />
    ));
};

    // Featured NFT component
    const renderFeaturedNft = () => {
        if (!featuredNft) return null;
        
        return (
            <motion.div 
                className="featured-artwork"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="featured-artwork-container">
                    {featuredNft.imageUrl ? (
                        <img 
                            src={featuredNft.imageUrl} 
                            alt={featuredNft.name || `NFT #${featuredNft.serialNumber}`}
                            className="featured-artwork-image"
                        />
                    ) : (
                        <div className="featured-artwork-image featured-nft-placeholder">
                            <FaImage className="placeholder-icon" />
                            <p>No image available</p>
                        </div>
                    )}
                </div>
                <div className="featured-artwork-info">
                    <p>NFT: {featuredNft.name || `Abandoned #${featuredNft.serialNumber}`}</p>
                    <p>From: The Immuta Collections Vol 1</p>
                </div>
            </motion.div>
        );
    };
    
    return (
        <div className="gallery-container">
            {/* Header with logo, dropdown, and menu */}
            <div className="main-header">               
                {/* Logo on left */}
                <div className="header-logo">
                    <img src={`${process.env.PUBLIC_URL}/logo.svg`} alt="Immuta" className="header-logo-img" />
                </div>
                
                {/* Dropdown in middle */}
                <div className="header-dropdown">
                    <button onClick={toggleDropdown} className="dropdown-button">
                        {selectedArtistId ? 
                            artists.find(a => a.artist_id === selectedArtistId)?.artist_name : 
                            'Select Artist'} <span className="dropdown-arrow">▼</span>
                    </button>
                    {dropdownVisible && (
                        <div className="dropdown-menu">
                            <div 
                                className="artist-name all-artists"
                                onClick={() => {
                                    setSelectedArtistId(null);
                                    setDropdownVisible(false);
                                }}
                            >
                                All Artists
                            </div>
                            {artists.map(artist => (
                                <div
                                    key={artist.artist_id}
                                    className='artist-name'
                                    onClick={() => {
                                        setSelectedArtistId(artist.artist_id);
                                        setDropdownVisible(false);
                                    }}
                                >
                                    {artist.artist_name}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Hamburger menu on right */}
                <div className="header-menu">
                    <div className="menu-icon" onClick={toggleMobileMenu}>
                        <FaBars />
                    </div>
                </div>
            
                
                {/* Mobile menu overlay */}
                {mobileMenuOpen && (
                    <div className="mobile-menu">
                        <div className="mobile-menu-items">
                            <div className="mobile-menu-item" onClick={() => {
                                window.location.href = '/';
                                setMobileMenuOpen(false);
                            }}>HOME</div>
                            <div className="mobile-menu-item" onClick={() => {
                                setMobileMenuOpen(false);
                            }}>COLLECT</div>
                            <div className="mobile-menu-item">
                                <a href="https://immuta.art/hash-gallery" target="_blank" rel="noopener noreferrer">EXHIBIT</a>
                            </div>
                            <div className="mobile-menu-item" onClick={() => {
                                // Dispatch custom event to open About popup
                                const aboutEvent = new CustomEvent('openAboutPopup');
                                window.dispatchEvent(aboutEvent);
                                setMobileMenuOpen(false);
                            }}>ABOUT</div>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Gallery Content */}
            <div className="gallery-content">
            <h2 className="hedera-section-title">The Immuta Collection Vol 1</h2>
            <p className="hedera-section-subtitle">Our Home Collection on Hedera</p>
            {/* Featured NFT from Hedera collection */}
            {featuredNft && !selectedArtistId && renderFeaturedNft()}
            
            {/* Hedera NFT Collection Display */}
               
                <div className="hedera-nft-grid">
                    {renderHederaNfts()}
                </div>
            
            
            {/* Artist artworks content */}
                {/* Artist selection message */}
                {selectedArtistId && (
                    <h2 className="gallery-title">
                        {artists.find(a => a.artist_id === selectedArtistId)?.artist_name}'s Gallery
                    </h2>
                )}
                
                {/* Empty state message when no artist is selected and no featured NFT */}
                {!selectedArtistId && !featuredNft && (
                    <div className="default-text">Loading The Immuta Collection Vol 1...</div>
                )}
            </div>
            
            {loading && <div className="loading-screen">Loading...</div>}
        </div>
    );
};

export default Gallery;
